import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, catchError, finalize, map } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import {
  AutoCompleteSelectionItem,
  IAutoCompleteService,
} from '../../../core/component/autocomplete-remote-paged/autocomplete-remote-paged.component';
import { AutoCompleteSelectionState } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';
import { ExecutorGroup } from '../../model/executor-group';
import { ExecutorGroupUser } from '../../model/executor-group-user';
import { DataService } from './../../../core/service/base/data.service';
import { MessageService } from './../../../core/service/message.service';
import { ProgressBarService } from './../../../core/service/progress-bar.service';

function toAutoCompleteExecutorGroupItem(executorGroup: ExecutorGroup): AutoCompleteSelectionItem<ExecutorGroup> {
  return new AutoCompleteSelectionItem(executorGroup.name, executorGroup.id, executorGroup);
}

@Injectable()
export class ExecutorGroupService extends DataService<ExecutorGroup> implements IAutoCompleteService {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, ExecutorGroup, progressBar, 'executor-group');
  }

  getSelectionItem(id: string, state: AutoCompleteSelectionState<any, any>): Observable<AutoCompleteSelectionItem<ExecutorGroup>> {
    return this.client.get<ExecutorGroup>(`${this.endpoint}/selectionItem/${id}`).pipe(
      map(toAutoCompleteExecutorGroupItem),
      catchError(e => {
        this.notification.showError(e);
        return [];
      })
    );
  }

  getSelection(filter: string, options: any, page?: number): Observable<AutoCompleteSelectionItem<any>[]> {
    const queryArgs = {
      filter: filter,
      maxItems: 20,
    };
    if (options) queryArgs['options'] = options;
    if (page > 0) queryArgs['page'] = page;

    return this.client.post<ExecutorGroup[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(toAutoCompleteExecutorGroupItem)),
      catchError(e => {
        this.notification.showError(e);
        throw e;
      })
    );
  }

  getWebUsersForExecutorGroup(executorGroupId: string): Observable<ExecutorGroupUser[]> {
    return this.client.get<ExecutorGroupUser[]>(`${this.endpoint}/${executorGroupId}/users`);
  }

  addWebUsersToExecutorGroup(executorGroupUser: ExecutorGroupUser): Observable<ExecutorGroupUser> {
    this.progressBar.start();
    return this.client
      .post<
        any[]
      >(`${this.endpoint}/executorgroupwebusers`, JSON.stringify(executorGroupUser), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  deleteWebUsersFromExecutorGroup(executorGroupWebUsersId: string): Observable<void> {
    this.progressBar.start();
    return this.client.delete<void>(`${this.endpoint}/executorgroupwebusers/${executorGroupWebUsersId}`).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }
}
